import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, FileText, Image as ImageIcon, Users, Lock, Server, ExternalLink, Mail, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import TextsTab from "@/components/admin/TextsTab";
import ServicesTab from "@/components/admin/ServicesTab";
import PhotosTab from "@/components/admin/PhotosTab";
import ClientsTab from "@/components/admin/ClientsTab";
import SecurityTab from "@/components/admin/SecurityTab";
import ContactsTab from "@/components/admin/ContactsTab";
import DeployTab from "@/components/admin/DeployTab";

const TABS = [
  { id: "texts", label: "Textos", icon: FileText, testid: "admin-tab-content" },
  { id: "services", label: "Servicios", icon: Server, testid: "admin-tab-services" },
  { id: "photos", label: "Fotos", icon: ImageIcon, testid: "admin-tab-photos" },
  { id: "clients", label: "Clientes", icon: Users, testid: "admin-tab-clients" },
  { id: "contacts", label: "Mensajes", icon: Mail, testid: "admin-tab-contacts" },
  { id: "security", label: "Seguridad", icon: Lock, testid: "admin-tab-security" },
  { id: "deploy", label: "Deploy", icon: Upload, testid: "admin-tab-deploy" },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("texts");
  const [user, setUser] = useState("");
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem("luva_token");
    if (!t) { nav("/admin"); return; }
    api.get("/auth/me").then((r) => { setUser(r.data.username); setChecking(false); })
      .catch(() => { localStorage.removeItem("luva_token"); nav("/admin"); });
  }, [nav]);

  const logout = () => {
    localStorage.removeItem("luva_token");
    localStorage.removeItem("luva_user");
    toast.success("Sesión cerrada");
    nav("/admin");
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center bg-base text-mute text-sm">Verificando sesión…</div>;

  return (
    <div className="min-h-screen flex bg-base">
      <aside className="w-64 border-r border-line-2 bg-surface p-5 hidden md:flex flex-col">
        <Link to="/" className="flex items-center gap-3 mb-8">
          <div className="w-9 h-9 bg-brand flex items-center justify-center text-white font-bold text-[14px]" style={{ borderRadius: 2 }}>LU</div>
          <div>
            <div className="text-fg font-semibold leading-none">LUVA</div>
            <div className="text-mute text-[11px] tracking-wider mt-1">ADMIN</div>
          </div>
        </Link>
        <nav className="flex-1 space-y-1">
          {TABS.map((t) => (
            <button key={t.id} data-testid={t.testid} onClick={() => setTab(t.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 text-[14px] transition-colors ${tab === t.id ? "bg-brand text-white" : "text-fg-2 hover:bg-base hover:text-fg"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="pt-4 border-t border-line-2 space-y-2">
          <a href="/" className="flex items-center gap-2 text-[12px] text-mute hover:text-fg px-3 py-2"><ExternalLink className="w-3 h-3" /> ver sitio</a>
          <div className="px-3 py-2 text-[12px] text-mute">Sesión: <span className="text-fg">{user}</span></div>
          <button data-testid={ADMIN.logout} onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 text-[14px] text-brand hover:bg-base">
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 left-0 right-0 bg-surface z-40 p-3 flex items-center justify-between border-b border-line-2">
        <Link to="/" className="text-fg font-semibold">LUVA admin</Link>
        <button data-testid={ADMIN.logout} onClick={logout} className="text-brand text-sm flex items-center gap-1"><LogOut className="w-4 h-4" /> Salir</button>
      </div>
      <div className="md:hidden fixed top-14 left-0 right-0 z-30 overflow-x-auto bg-surface border-b border-line-2">
        <div className="flex gap-1 px-3 py-2 min-w-max">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-1.5 text-xs whitespace-nowrap ${tab === t.id ? "bg-brand text-white" : "text-fg-2"}`}>{t.label}</button>
          ))}
        </div>
      </div>

      <main className="flex-1 p-6 md:p-10 pt-28 md:pt-10 overflow-auto">
        <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {tab === "texts" && <TextsTab />}
          {tab === "services" && <ServicesTab />}
          {tab === "photos" && <PhotosTab />}
          {tab === "clients" && <ClientsTab />}
          {tab === "contacts" && <ContactsTab />}
          {tab === "security" && <SecurityTab />}
          {tab === "deploy" && <DeployTab />}
        </motion.div>
      </main>
    </div>
  );
}
